import { useState, type ReactNode, type SelectHTMLAttributes } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { QRCodeSVG } from 'qrcode.react';
import { Check, ChevronDown, Globe, ShieldCheck, SlidersHorizontal, User, Users } from 'lucide-react';
import {
  Badge,
  Button,
  Card,
  ErrorState,
  Field,
  LoadingState,
  SectionHeader,
  SelectField,
} from '@pixlide/ui';
import { ApiError } from '@/api/client';
import { formatDate } from '@/lib/format';
import { useAuthStore, useHasPermission, type CurrentUser } from '@/stores/auth-store';
import { useUsers } from '@/api/queries/settings';
import { useClearSupportHistory } from '@/api/queries/support';
import { canNotifyNatively } from '@/components/layout/useNotificationAlerts';
import { usePreferencesStore, type AccentColor, type Preferences } from '@/stores/preferences-store';
import {
  useChangePassword,
  useDisableTwoFactor,
  useEnableTwoFactor,
  useExternalAuthProviders,
  useRequestChangePassword,
  useRequestTwoFactorEmailCode,
  useSetupTwoFactor,
  useStartExternalLink,
  useUnlinkExternalProvider,
  useUpdateCompanyDetails,
  useUpdateProfile,
  useUpdateTimeZone,
} from '@/api/queries/auth';
import { DialCodeSelect } from '@/features/auth/DialCodeSelect';
import { firstUnsatisfiedRule } from '@/features/auth/password-rules';
import { ProviderButton, ProviderIcon } from '@/features/auth/SocialAuthButtons';
import { providerLabel, rememberPendingExternalAuth } from '@/features/auth/external-provider';
import { COUNTRIES, JOB_TITLES, OTHER_JOB_TITLE, splitPhoneNumber } from '@/features/auth/job-titles';
import {
  profileIdentitySchema,
  registerProfileSchema,
  type RegisterProfileInput,
} from '@/features/auth/schemas';

const TABS = [
  { key: 'profil', label: 'Profil', icon: User },
  { key: 'preferences', label: 'Préférences', icon: SlidersHorizontal },
  { key: 'securite', label: 'Sécurité', icon: ShieldCheck },
  { key: 'equipe', label: 'Équipe', icon: Users },
] as const;

type TabKey = (typeof TABS)[number]['key'];

/**
 * Rôle affiché à l'utilisateur, dérivé de l'enum `UserRole` côté C# : 0 = Viewer,
 * 1 = Editor, 2 = Manager, 3 = Owner, 100 = PlatformAdmin — mêmes libellés que le
 * back-office. Un rôle inconnu retombe sur son numéro brut plutôt qu'un libellé inventé.
 */
const ROLE_LABELS: Record<number, string> = {
  0: 'Lecture seule',
  1: 'Éditeur',
  2: 'Manager',
  3: 'Propriétaire',
  100: 'Admin plateforme',
};

/** Interrupteur de la maquette : pilule noire, pastille blanche. */
function Toggle({
  checked,
  label,
  disabled,
  onChange,
}: {
  checked: boolean;
  label: string;
  disabled?: boolean;
  onChange?: (next: boolean) => void;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => onChange?.(!checked)}
      className={`relative h-7 w-12 shrink-0 rounded-pill transition-colors focus-visible:ring-2 focus-visible:ring-ink-900 focus-visible:ring-offset-2 focus-visible:outline-none ${
        checked ? 'bg-ink-900' : 'bg-ink-200'
      } ${disabled ? 'cursor-not-allowed opacity-50' : ''}`}
    >
      <span
        className={`absolute top-1 left-0 size-5 rounded-full bg-white shadow transition-transform ${
          checked ? 'translate-x-6' : 'translate-x-1'
        }`}
      />
    </button>
  );
}

/**
 * Ligne de réglage de la maquette : titre et description à gauche, contrôle à droite,
 * séparée de la suivante par un filet.
 */
function SettingRow({
  title,
  description,
  badge,
  control,
  children,
}: {
  title: string;
  description?: ReactNode;
  badge?: string;
  control?: ReactNode;
  children?: ReactNode;
}) {
  return (
    <div className="border-b border-ink-100/70 py-5 first:pt-0 last:border-b-0">
      <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <h3 className="font-semibold text-ink-900">{title}</h3>
            {badge === undefined ? null : <Badge tone="neutral">{badge}</Badge>}
          </div>
          {description === undefined ? null : (
            <div className="mt-0.5 text-sm font-medium text-ink-500">{description}</div>
          )}
        </div>
        {control}
      </div>
      {children}
    </div>
  );
}

/** Liste déroulante compacte, alignée à droite d'une `SettingRow`. */
function InlineSelect({
  label,
  icon,
  className,
  children,
  ...props
}: SelectHTMLAttributes<HTMLSelectElement> & { label: string; icon?: ReactNode }) {
  return (
    <div className="relative flex items-center gap-2 text-sm font-medium text-ink-900">
      {icon}
      <select
        aria-label={label}
        className={`cursor-pointer appearance-none bg-transparent py-1 pr-6 text-right focus-visible:outline-none disabled:cursor-default ${className ?? ''}`}
        {...props}
      >
        {children}
      </select>
      <ChevronDown aria-hidden className="pointer-events-none absolute right-0 size-4 text-ink-500" />
    </div>
  );
}

/** Pré-remplit le formulaire avec le profil tel que le serveur l'a stocké. */
function toFormValues(user: CurrentUser): RegisterProfileInput {
  const phone = splitPhoneNumber(user.phoneNumber);
  const jobTitle = user.jobTitle ?? '';

  // Un poste hors liste a été saisi via « Autre » : il est rendu dans le champ libre,
  // sans quoi la liste déroulante l'afficherait vide et l'enregistrement l'effacerait.
  const isListed = jobTitle === '' || (JOB_TITLES as readonly string[]).includes(jobTitle);

  return {
    firstName: user.firstName,
    lastName: user.lastName,
    jobTitle: isListed ? jobTitle : OTHER_JOB_TITLE,
    customJobTitle: isListed ? '' : jobTitle,
    companyName: user.companyName,
    dialCode: phone.dialCode,
    phoneNumber: phone.number,

    // Le champ affiche déjà « https:// » en préfixe ; une adresse en http:// est laissée
    // entière pour ne pas changer de schéma à l'enregistrement.
    websiteUrl: (user.websiteUrl ?? '').replace(/^https:\/\//, '').replace(/\/$/, ''),
    city: user.city ?? '',
    countryCode: user.countryCode,
  };
}

function ProfilTab() {
  const user = useAuthStore((state) => state.user);

  return user === null ? <LoadingState /> : <ProfileForm key={user.userId} user={user} />;
}

/**
 * Profil, conforme à la maquette : avatar puis « Vos informations ».
 *
 * Le formulaire mêle deux natures de données. Prénom, nom et poste appartiennent à
 * l'utilisateur, qui peut toujours les corriger. Raison sociale, téléphone, site, ville et
 * pays appartiennent au COMPTE, partagé par toute l'équipe : seul le propriétaire
 * (`billing:manage`) les modifie, les autres membres les voient en lecture seule. Un seul
 * bouton « Sauvegarder » envoie les deux, comme sur la maquette.
 */
function ProfileForm({ user }: { user: CurrentUser }) {
  const canEditCompany = useHasPermission('billing:manage');
  const updateProfile = useUpdateProfile();
  const updateCompany = useUpdateCompanyDetails();

  const [saved, setSaved] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    setError,
    watch,
    formState: { errors, isDirty, isSubmitting },
  } = useForm<RegisterProfileInput>({
    resolver: zodResolver(canEditCompany ? registerProfileSchema : profileIdentitySchema),
    defaultValues: toFormValues(user),
  });

  const needsCustomJobTitle = watch('jobTitle') === OTHER_JOB_TITLE;
  const countryCode = watch('countryCode');
  const isKnownCountry = COUNTRIES.some((country) => country.code === countryCode);

  const onSubmit = handleSubmit(async (values) => {
    setSaved(false);
    setFormError(null);

    try {
      let updated = await updateProfile.mutateAsync({
        firstName: values.firstName,
        lastName: values.lastName,
        jobTitle:
          values.jobTitle === OTHER_JOB_TITLE ? (values.customJobTitle ?? '').trim() : values.jobTitle,
      });

      if (canEditCompany) {
        updated = await updateCompany.mutateAsync({
          companyName: values.companyName,
          phoneNumber: `${values.dialCode} ${values.phoneNumber.trim()}`,
          websiteUrl: values.websiteUrl?.trim() || null,
          city: values.city?.trim() || null,
          countryCode: values.countryCode,
        });
      }

      reset(toFormValues(updated));
      setSaved(true);
    } catch (submitError) {
      // Les erreurs de validation du serveur sont rattachées à leur champ : les clés
      // renvoyées en camelCase sont exactement les noms du formulaire.
      if (submitError instanceof ApiError && submitError.hasFieldErrors) {
        for (const [field, messages] of Object.entries(submitError.errors ?? {})) {
          if (field in values && messages[0] !== undefined) {
            setError(field as keyof RegisterProfileInput, { type: 'server', message: messages[0] });
          }
        }
      } else {
        setFormError(
          submitError instanceof ApiError
            ? submitError.message
            : "L'enregistrement a échoué. Réessayez dans un instant.",
        );
      }
    }
  });

  return (
    <div className="flex max-w-2xl flex-col gap-6">
      <section>
        <h3 className="font-semibold text-ink-900">Votre avatar</h3>
        <p className="text-sm font-medium text-ink-500">
          Votre profil aide les autres à vous reconnaître dans les discussions de groupe.
        </p>
        <span
          aria-hidden
          className="mt-3 grid size-16 place-items-center rounded-pill bg-brand-100 text-lg font-semibold text-brand-700"
        >
          {user.initials}
        </span>
      </section>

      <section className="border-t border-ink-100/70 pt-5">
        <h3 className="font-semibold text-ink-900">Vos informations</h3>
        <p className="text-sm text-ink-500">Complétez ou mettez à jour vos informations.</p>

        {formError ? (
          <p
            role="alert"
            className="mt-4 rounded-xl bg-danger-100 px-4 py-3 text-[13px] font-medium text-danger-500"
          >
            {formError}
          </p>
        ) : null}

        <form onSubmit={onSubmit} noValidate className="mt-4 flex flex-col gap-3">
          <div className="grid gap-3 sm:grid-cols-2">
            <Field
              label="Prénom"
              autoComplete="given-name"
              required
              error={errors.firstName?.message}
              {...register('firstName')}
            />
            <Field
              label="Nom"
              autoComplete="family-name"
              required
              error={errors.lastName?.message}
              {...register('lastName')}
            />
          </div>

          <SelectField label="Poste occupé" required error={errors.jobTitle?.message} {...register('jobTitle')}>
            <option value="">Sélectionnez votre poste</option>
            {JOB_TITLES.map((title) => (
              <option key={title} value={title}>
                {title}
              </option>
            ))}
            <option value={OTHER_JOB_TITLE}>{OTHER_JOB_TITLE}</option>
          </SelectField>

          {needsCustomJobTitle ? (
            <Field
              label="Intitulé de votre poste"
              required
              placeholder="Notez l’intitulé de votre poste"
              error={errors.customJobTitle?.message}
              {...register('customJobTitle')}
            />
          ) : null}

          <div className="grid gap-3 sm:grid-cols-[minmax(0,0.78fr)_minmax(0,1.22fr)]">
            <Field
              label="Entreprise"
              autoComplete="organization"
              required={canEditCompany}
              readOnly={!canEditCompany}
              error={errors.companyName?.message}
              {...register('companyName')}
            />

            {canEditCompany ? (
              <div className="flex items-stretch gap-2">
                <DialCodeSelect {...register('dialCode')} />
                <Field
                  label="N° téléphone"
                  type="tel"
                  autoComplete="tel-national"
                  required
                  className="min-w-0 flex-1"
                  placeholder="04 22 11 22 11"
                  error={errors.phoneNumber?.message}
                  {...register('phoneNumber')}
                />
              </div>
            ) : (
              <Field label="N° téléphone" readOnly prefix={watch('dialCode')} {...register('phoneNumber')} />
            )}
          </div>

          <Field
            label="Site web"
            type="url"
            autoComplete="url"
            prefix="https://"
            placeholder={canEditCompany ? 'pitaya.fr' : undefined}
            readOnly={!canEditCompany}
            error={errors.websiteUrl?.message}
            {...register('websiteUrl')}
          />

          <div className="grid gap-3 sm:grid-cols-2">
            <Field
              label="Ville"
              autoComplete="address-level2"
              placeholder={canEditCompany ? 'Bordeaux' : undefined}
              readOnly={!canEditCompany}
              error={errors.city?.message}
              {...register('city')}
            />

            {canEditCompany ? (
              <SelectField label="Pays" error={errors.countryCode?.message} {...register('countryCode')}>
                {/* Un code stocké hors de la liste reste sélectionné tel quel plutôt que
                    d'être remplacé en silence par le premier pays proposé. */}
                {isKnownCountry ? null : <option value={countryCode}>{countryCode}</option>}
                {COUNTRIES.map((country) => (
                  <option key={country.code} value={country.code}>
                    {country.label}
                  </option>
                ))}
              </SelectField>
            ) : (
              <Field
                label="Pays"
                readOnly
                value={COUNTRIES.find((country) => country.code === countryCode)?.label ?? countryCode}
              />
            )}
          </div>

          {canEditCompany ? null : (
            <p className="text-xs text-ink-500">
              Les coordonnées de l'entreprise sont partagées par toute l'équipe : seul le
              propriétaire du compte peut les modifier.
            </p>
          )}

          <div className="mt-3 flex items-center justify-end gap-4">
            {saved && !isDirty ? (
              <p role="status" className="mr-auto text-xs font-medium text-success-600">
                Modifications enregistrées.
              </p>
            ) : null}

            <Button
              type="button"
              variant="ghost"
              disabled={!isDirty || isSubmitting}
              onClick={() => {
                reset(toFormValues(user));
                setFormError(null);
              }}
            >
              Annuler
            </Button>
            <Button type="submit" className="min-w-40" isLoading={isSubmitting} disabled={!isDirty}>
              Sauvegarder
            </Button>
          </div>
        </form>
      </section>
    </div>
  );
}

// ============================================================================
// Préférences
// ============================================================================

const THEMES = [
  { key: 'light', label: 'Clair' },
  { key: 'dark', label: 'Sombre' },
  { key: 'auto', label: 'Automatique' },
] as const;

/**
 * Miniature d'interface de la maquette, dessinée en CSS plutôt qu'en image. Couleurs
 * FIXES et non jetons : en thème sombre, la miniature « Clair » doit rester claire.
 */
function ThemePreview({ variant }: { variant: (typeof THEMES)[number]['key'] }) {
  const pane = (dark: boolean) => (
    <div className={`flex flex-1 gap-1.5 p-2 ${dark ? 'bg-[#16162a]' : 'bg-[#ffffff]'}`}>
      <div className="flex w-1/3 flex-col gap-1">
        <span className="flex gap-0.5">
          <span className="size-1 rounded-full bg-danger-500" />
          <span className="size-1 rounded-full bg-warning-500" />
          <span className="size-1 rounded-full bg-success-500" />
        </span>
        {[0, 1, 2, 3].map((line) => (
          <span key={line} className={`h-1 rounded-full ${dark ? 'bg-[#2b2b41]' : 'bg-[#e6e6ed]'}`} />
        ))}
      </div>
      <div className="flex flex-1 flex-col gap-1 pt-2">
        <span className={`size-2 rounded-full ${dark ? 'bg-[#56566f]' : 'bg-[#cfcfdc]'}`} />
        {[0, 1, 2, 3, 4].map((line) => (
          <span key={line} className={`h-1 rounded-full ${dark ? 'bg-[#2b2b41]' : 'bg-[#e6e6ed]'}`} />
        ))}
      </div>
    </div>
  );

  return (
    <span aria-hidden className="flex h-24 overflow-hidden rounded-xl">
      {variant === 'auto' ? (
        <>
          {pane(false)}
          {pane(true)}
        </>
      ) : (
        pane(variant === 'dark')
      )}
    </span>
  );
}

/**
 * Fuseaux proposés : les marchés desservis et l'outre-mer français, où un groupe peut avoir
 * des établissements. Un fuseau déjà enregistré hors de cette liste reste affiché.
 */
const TIME_ZONES = [
  { id: 'Europe/Paris', city: 'Paris' },
  { id: 'Europe/Brussels', city: 'Bruxelles' },
  { id: 'Europe/Zurich', city: 'Zurich' },
  { id: 'Europe/Luxembourg', city: 'Luxembourg' },
  { id: 'Europe/Monaco', city: 'Monaco' },
  { id: 'Europe/London', city: 'Londres' },
  { id: 'Europe/Madrid', city: 'Madrid' },
  { id: 'Europe/Rome', city: 'Rome' },
  { id: 'Europe/Berlin', city: 'Berlin' },
  { id: 'Europe/Lisbon', city: 'Lisbonne' },
  { id: 'America/Toronto', city: 'Montréal' },
  { id: 'America/Guadeloupe', city: 'Guadeloupe' },
  { id: 'America/Martinique', city: 'Martinique' },
  { id: 'America/Cayenne', city: 'Guyane' },
  { id: 'Indian/Reunion', city: 'La Réunion' },
  { id: 'Indian/Mayotte', city: 'Mayotte' },
  { id: 'Pacific/Noumea', city: 'Nouméa' },
  { id: 'Pacific/Tahiti', city: 'Tahiti' },
] as const;

/** « Paris (UTC+1) - 13:00 », comme sur la maquette : décalage et heure actuels du fuseau. */
function timeZoneLabel(id: string, city: string, now: Date): string {
  try {
    const offset =
      new Intl.DateTimeFormat('fr-FR', { timeZone: id, timeZoneName: 'shortOffset' })
        .formatToParts(now)
        .find((part) => part.type === 'timeZoneName')?.value ?? '';
    const time = new Intl.DateTimeFormat('fr-FR', { timeZone: id, hour: '2-digit', minute: '2-digit' }).format(now);

    return `${city} (${offset}) - ${time}`;
  } catch {
    // Fuseau inconnu du navigateur : l'identifiant brut vaut mieux qu'une ligne vide.
    return city;
  }
}

const ACCENTS: readonly { key: AccentColor; label: string }[] = [
  { key: 'system', label: 'Système' },
  { key: 'blue', label: 'Bleu' },
  { key: 'green', label: 'Vert' },
  { key: 'orange', label: 'Orange' },
  { key: 'pink', label: 'Rose' },
];

/** Le navigateur sait-il seulement afficher des notifications natives ? */
const supportsNativeNotifications = typeof window.Notification === 'function';

/**
 * Préférences, conformes à la maquette.
 *
 * Deux natures de réglages derrière un même « Sauvegarder » :
 *  - thème, couleur d'accent et notifications appartiennent à l'APPAREIL (voir
 *    `preferences-store`) et s'appliquent dès l'enregistrement ;
 *  - le fuseau horaire appartient au COMPTE (il donne leur sens aux plages de diffusion) et
 *    n'est modifiable que par le propriétaire.
 * « Tout supprimer » (Chat) agit immédiatement, après confirmation : ce n'est pas un réglage
 * qu'on prépare puis enregistre. Le français est la seule traduction livrée, d'où une liste
 * de langues à une entrée (cf. `LanguageSwitcher`).
 */
function PreferencesTab() {
  const user = useAuthStore((state) => state.user);
  const canManageAccount = useHasPermission('billing:manage');
  const updateTimeZone = useUpdateTimeZone();

  const stored = usePreferencesStore(
    useShallow((state) => ({
      theme: state.theme,
      accent: state.accent,
      osNotifications: state.osNotifications,
      browserNotifications: state.browserNotifications,
    })),
  );
  const setPreferences = usePreferencesStore((state) => state.setPreferences);

  const savedTimeZone = user?.timeZoneId ?? 'Europe/Paris';
  const [draft, setDraft] = useState<Preferences>(stored);
  const [timeZone, setTimeZone] = useState(savedTimeZone);
  const [saved, setSaved] = useState(false);
  const [notificationError, setNotificationError] = useState<string | null>(null);

  const now = new Date();
  const isDirty =
    timeZone !== savedTimeZone ||
    (Object.keys(draft) as (keyof Preferences)[]).some((key) => draft[key] !== stored[key]);
  const options = TIME_ZONES.some((zone) => zone.id === savedTimeZone)
    ? TIME_ZONES
    : [{ id: savedTimeZone, city: savedTimeZone }, ...TIME_ZONES];

  const timeZoneError = updateTimeZone.error instanceof ApiError ? updateTimeZone.error.message : null;

  function change(patch: Partial<Preferences>) {
    setDraft((current) => ({ ...current, ...patch }));
    setSaved(false);
  }

  async function save() {
    setSaved(false);
    setNotificationError(null);

    let next = draft;

    // L'autorisation est demandée EN PREMIER, avant tout `await` : certains navigateurs
    // (Safari) ne l'accordent que dans le prolongement direct du clic.
    if (draft.osNotifications && !stored.osNotifications && !canNotifyNatively()) {
      const permission = supportsNativeNotifications ? await window.Notification.requestPermission() : 'denied';

      if (permission !== 'granted') {
        next = { ...draft, osNotifications: false };
        setDraft(next);
        setNotificationError(
          "Votre navigateur n'autorise pas les notifications pour Pixlide : autorisez-les dans les réglages du site, puis réessayez.",
        );
      }
    }

    setPreferences(next);

    if (timeZone !== savedTimeZone) {
      await updateTimeZone.mutateAsync({ timeZoneId: timeZone });
    }

    setSaved(true);
  }

  return (
    <div className="flex max-w-2xl flex-col">
      <SettingRow title="Thème de l'interface" description="Choisissez l'apparence du mode clair ou sombre.">
        <div className="mt-4 grid grid-cols-3 gap-3 sm:gap-5">
          {THEMES.map((theme) => {
            const isActive = theme.key === draft.theme;

            return (
              <button
                key={theme.key}
                type="button"
                aria-pressed={isActive}
                onClick={() => change({ theme: theme.key })}
                className="flex flex-col gap-2 text-center text-sm"
              >
                <span
                  className={`relative block rounded-[14px] p-0.5 transition ${
                    isActive ? 'ring-2 ring-ink-900' : 'ring-1 ring-ink-100 hover:ring-ink-300'
                  }`}
                >
                  <ThemePreview variant={theme.key} />
                  {isActive ? (
                    <span className="absolute top-1.5 right-1.5 grid size-5 place-items-center rounded-full bg-ink-900 text-white">
                      <Check aria-hidden className="size-3" />
                    </span>
                  ) : null}
                </span>
                <span className={isActive ? 'font-medium text-ink-900' : 'text-ink-500'}>{theme.label}</span>
              </button>
            );
          })}
        </div>
      </SettingRow>

      <SettingRow
        title="Personnaliser le thème"
        description="Choisissez un style coloré de l'interface."
        control={
          <InlineSelect
            label="Couleur d'accent de l'interface"
            icon={<span aria-hidden className="size-3 rounded-full bg-brand-500" />}
            value={draft.accent}
            onChange={(event) => change({ accent: event.target.value as AccentColor })}
          >
            {ACCENTS.map((accent) => (
              <option key={accent.key} value={accent.key}>
                {accent.label}
              </option>
            ))}
          </InlineSelect>
        }
      />

      <SettingRow
        title="Langue"
        description="La langue dans laquelle Pixlide s'affiche."
        control={
          <InlineSelect label="Langue de l'interface" disabled defaultValue="fr" icon={<span aria-hidden>🇫🇷</span>}>
            <option value="fr">Français</option>
          </InlineSelect>
        }
      />

      <SettingRow
        title="Fuseau horaire"
        description={
          canManageAccount
            ? 'Sélectionnez votre fuseau horaire.'
            : 'Fuseau de référence du compte, modifiable par son propriétaire.'
        }
        control={
          <InlineSelect
            label="Fuseau horaire"
            icon={<Globe aria-hidden className="size-4 text-ink-300" />}
            disabled={!canManageAccount}
            value={timeZone}
            onChange={(event) => {
              setTimeZone(event.target.value);
              setSaved(false);
              updateTimeZone.reset();
            }}
          >
            {options.map((zone) => (
              <option key={zone.id} value={zone.id}>
                {timeZoneLabel(zone.id, zone.city, now)}
              </option>
            ))}
          </InlineSelect>
        }
      >
        {timeZoneError ? (
          <p role="alert" className="mt-2 text-xs font-medium text-danger-500">
            {timeZoneError}
          </p>
        ) : null}
      </SettingRow>

      <SettingRow title="Notifications">
        <div className="mt-1 flex flex-col gap-2 text-sm font-medium text-ink-500">
          <div className="flex items-center justify-between gap-6">
            <span>Activez les notifications sur le système d'exploitation.</span>
            <InlineSelect
              label="Notifications du système d'exploitation"
              disabled={!supportsNativeNotifications}
              value={draft.osNotifications ? 'on' : 'off'}
              onChange={(event) => {
                change({ osNotifications: event.target.value === 'on' });
                setNotificationError(null);
              }}
            >
              <option value="on">Activé</option>
              <option value="off">{supportsNativeNotifications ? 'Désactivé' : 'Non pris en charge'}</option>
            </InlineSelect>
          </div>
          <div className="flex items-center justify-between gap-6">
            <span>Activez les notifications sur le navigateur web.</span>
            <InlineSelect
              label="Notifications dans le navigateur"
              value={draft.browserNotifications ? 'on' : 'off'}
              onChange={(event) => change({ browserNotifications: event.target.value === 'on' })}
            >
              <option value="on">Activé</option>
              <option value="off">Désactivé</option>
            </InlineSelect>
          </div>
          {notificationError ? (
            <p role="alert" className="text-xs font-medium text-danger-500">
              {notificationError}
            </p>
          ) : null}
        </div>
      </SettingRow>

      <ClearChatRow />

      <ExternalAccountsSection />

      <div className="mt-6 flex items-center justify-end gap-4">
        {saved && !isDirty ? (
          <p role="status" className="mr-auto text-xs font-medium text-success-600">
            Préférences enregistrées.
          </p>
        ) : null}
        <Button
          type="button"
          variant="ghost"
          disabled={!isDirty || updateTimeZone.isPending}
          onClick={() => {
            setDraft(stored);
            setTimeZone(savedTimeZone);
            setNotificationError(null);
            updateTimeZone.reset();
          }}
        >
          Annuler
        </Button>
        <Button
          type="button"
          className="min-w-40"
          disabled={!isDirty}
          isLoading={updateTimeZone.isPending}
          onClick={() => void save().catch(() => undefined)}
        >
          Sauvegarder
        </Button>
      </div>
    </div>
  );
}

/**
 * « Chat › Supprimer tous les messages ». Confirmation en deux temps, sur place : l'action
 * est irréversible pour l'utilisateur, un clic égaré ne doit pas suffire.
 */
function ClearChatRow() {
  const clearHistory = useClearSupportHistory();
  const [isConfirming, setIsConfirming] = useState(false);

  const error = clearHistory.error instanceof ApiError ? clearHistory.error.message : null;

  return (
    <SettingRow
      title="Chat"
      description={
        clearHistory.isSuccess
          ? 'Vos messages ont été supprimés de votre messagerie.'
          : isConfirming
            ? "Vos échanges avec le support disparaîtront de votre messagerie. Le reste de l'équipe les conserve."
            : 'Supprimer tous les messages.'
      }
      control={
        isConfirming ? (
          <div className="flex items-center gap-2">
            <Button type="button" variant="ghost" size="sm" onClick={() => setIsConfirming(false)}>
              Annuler
            </Button>
            <button
              type="button"
              disabled={clearHistory.isPending}
              onClick={() =>
                void clearHistory
                  .mutateAsync()
                  .then(() => setIsConfirming(false))
                  .catch(() => undefined)
              }
              className="h-7 rounded-pill bg-danger-500 px-4 text-xs font-medium text-white transition hover:opacity-90 disabled:opacity-60"
            >
              {clearHistory.isPending ? 'Suppression…' : 'Confirmer la suppression'}
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => {
              clearHistory.reset();
              setIsConfirming(true);
            }}
            className="h-7 rounded-pill bg-danger-500 px-4 text-xs font-medium text-white transition hover:opacity-90"
          >
            Tout supprimer
          </button>
        )
      }
    >
      {error ? (
        <p role="alert" className="mt-2 text-xs font-medium text-danger-500">
          {error}
        </p>
      ) : null}
    </SettingRow>
  );
}

/** Fournisseurs présentés par la maquette, configurés ou non. */
const KNOWN_PROVIDERS = ['google', 'linkedin'] as const;

/**
 * « Connexion avec des applications tierces » : association / dissociation d'un fournisseur
 * de connexion externe.
 *
 * Google et LinkedIn sont toujours affichés, comme sur la maquette, mais seuls ceux que le
 * serveur déclare configurés sont cliquables : un bouton actif qui échouerait chez le
 * fournisseur ferait perdre l'aller-retour. Un fournisseur non configuré l'indique sous son
 * bouton plutôt que de disparaître, pour qu'on sache que l'option existe.
 *
 * Les deux opérations exigent le mot de passe du compte, et ce n'est pas une formalité :
 * associer un fournisseur ajoute un moyen de connexion qui SURVIVRAIT à un changement de
 * mot de passe. Sans cette preuve, une session laissée ouverte — poste non verrouillé,
 * jeton volé — suffirait à installer un accès durable. Même exigence que la désactivation
 * de la 2FA, pour la même raison. Le domaine n'autorise qu'un fournisseur par compte : les
 * autres boutons restent désactivés tant qu'un fournisseur est associé.
 */
function ExternalAccountsSection() {
  const user = useAuthStore((state) => state.user);
  const providers = useExternalAuthProviders();
  const startLink = useStartExternalLink();
  const unlink = useUnlinkExternalProvider();

  const [pending, setPending] = useState<{ intent: 'link' | 'unlink'; provider: string } | null>(null);
  const [password, setPassword] = useState('');

  const linked = user?.externalProvider ?? null;
  const configured = providers.data ?? [];

  // Les fournisseurs de la maquette, puis tout autre fournisseur configuré, puis celui qui
  // est associé même si l'exploitation l'a depuis désactivé : il doit pouvoir être retiré.
  const listed = [...new Set([...KNOWN_PROVIDERS, ...configured, ...(linked === null ? [] : [linked])])];
  const unconfigured = listed.filter((provider) => provider !== linked && !configured.includes(provider));

  function close() {
    setPending(null);
    setPassword('');
    startLink.reset();
    unlink.reset();
  }

  async function confirm() {
    if (pending === null) {
      return;
    }

    if (pending.intent === 'unlink') {
      await unlink.mutateAsync({ password });
      close();

      return;
    }

    const { authorizationUrl } = await startLink.mutateAsync({ provider: pending.provider, password });

    rememberPendingExternalAuth({ intent: 'link', provider: pending.provider });
    window.location.assign(authorizationUrl);
  }

  const error =
    (startLink.error instanceof ApiError ? startLink.error.message : null) ??
    (unlink.error instanceof ApiError ? unlink.error.message : null);

  return (
    <SettingRow
      title="Connexion avec des applications tierces"
      description={
        linked === null
          ? "Connectez-vous via l'un de vos comptes. La vérification en deux étapes, si elle est activée, reste demandée."
          : `Votre compte ${providerLabel(linked)} peut ouvrir une session Pixlide. Dissociez-le pour ne plus autoriser que le mot de passe.`
      }
    >
      <div className="mt-4 flex max-w-md flex-col gap-2.5">
        {listed.map((provider) => {
          const isLinked = provider === linked;

          return (
            <ProviderButton
              key={provider}
              label={
                isLinked
                  ? `${providerLabel(provider)} associé · Dissocier`
                  : `Se connecter avec ${providerLabel(provider)}`
              }
              icon={<ProviderIcon provider={provider} />}
              loading={false}
              disabled={
                pending !== null ||
                (linked !== null && !isLinked) ||
                (!isLinked && !configured.includes(provider))
              }
              onClick={() => setPending({ intent: isLinked ? 'unlink' : 'link', provider })}
            />
          );
        })}

        {providers.isSuccess && unconfigured.length > 0 ? (
          <p className="text-xs text-ink-500">
            {unconfigured.map(providerLabel).join(' et ')}{' '}
            {unconfigured.length > 1 ? 'ne sont pas encore configurés' : "n'est pas encore configuré"} sur
            ce serveur : la connexion sera disponible dès que l'administrateur l'aura activée.
          </p>
        ) : null}

        {linked !== null && listed.length > 1 ? (
          <p className="text-xs text-ink-500">
            Un seul compte externe peut être associé : dissociez {providerLabel(linked)} pour en
            choisir un autre.
          </p>
        ) : null}

        {pending === null ? null : (
          <div className="mt-2 flex flex-col gap-3">
            <Field
              label="Mot de passe actuel"
              type="password"
              revealable
              autoComplete="current-password"
              autoFocus
              error={error ?? undefined}
              value={password}
              onChange={(event) => {
                setPassword(event.target.value);
                startLink.reset();
                unlink.reset();
              }}
            />

            <div className="flex gap-3">
              <Button type="button" variant="secondary" size="sm" onClick={close}>
                Annuler
              </Button>
              <Button
                size="sm"
                isLoading={startLink.isPending || unlink.isPending}
                disabled={!password}
                onClick={() => void confirm().catch(() => undefined)}
              >
                {pending.intent === 'unlink' ? 'Dissocier' : `Continuer vers ${providerLabel(pending.provider)}`}
              </Button>
            </div>
          </div>
        )}
      </div>
    </SettingRow>
  );
}

// ============================================================================
// Sécurité
// ============================================================================

/**
 * Étapes de l'activation 2FA. `idle` couvre à la fois « désactivée » et « activée » — c'est
 * `user.twoFactorEnabled` qui distingue les deux, `awaitingEmailCode`, `enrolling` et
 * `recoveryCodes` ne sont visités qu'une fois, le temps de l'activation.
 */
type TwoFactorStep =
  | { kind: 'idle' }
  | { kind: 'awaitingEmailCode' }
  | { kind: 'enrolling'; secret: string; provisioningUri: string }
  | { kind: 'recoveryCodes'; codes: readonly string[] };

/**
 * Ligne « Authentification à deux facteurs » de la maquette, pilotée par l'interrupteur.
 *
 * L'interrupteur reflète l'état RÉEL du compte : l'actionner ouvre le parcours sous la
 * ligne, il ne bascule qu'une fois ce parcours abouti.
 *
 * Activation en trois temps : un code envoyé par email prouve d'abord l'accès à la boîte
 * mail (même idiome que le changement de mot de passe), avant de configurer et confirmer
 * l'application d'authentification. Sans cette étape, un attaquant qui a déjà pris la main
 * sur une session pourrait activer la 2FA avec SON application, verrouillant le
 * propriétaire légitime hors de son propre compte. La désactivation exige le mot de passe
 * ET un code du second facteur.
 */
function TwoFactorSection() {
  const user = useAuthStore((state) => state.user);
  const requestEmailCode = useRequestTwoFactorEmailCode();
  const setup = useSetupTwoFactor();
  const enable = useEnableTwoFactor();
  const disable = useDisableTwoFactor();

  const [step, setStep] = useState<TwoFactorStep>({ kind: 'idle' });
  const [emailCode, setEmailCode] = useState('');
  const [code, setCode] = useState('');
  const [disablePassword, setDisablePassword] = useState('');
  const [disableCode, setDisableCode] = useState('');
  const [showDisableForm, setShowDisableForm] = useState(false);

  const isEnabled = user?.twoFactorEnabled === true;
  const isBusy = step.kind !== 'idle' || showDisableForm || requestEmailCode.isPending;

  function cancelEnrollment() {
    setEmailCode('');
    setCode('');
    setup.reset();
    enable.reset();
    setStep({ kind: 'idle' });
  }

  function cancelDisable() {
    setDisablePassword('');
    setDisableCode('');
    disable.reset();
    setShowDisableForm(false);
  }

  async function startEmailVerification() {
    await requestEmailCode.mutateAsync();
    setStep({ kind: 'awaitingEmailCode' });
  }

  async function startEnrollment() {
    const result = await setup.mutateAsync();
    setStep({ kind: 'enrolling', secret: result.secret, provisioningUri: result.provisioningUri });
  }

  async function confirmEnrollment() {
    const result = await enable.mutateAsync({ emailCode, code });
    setEmailCode('');
    setCode('');
    setStep({ kind: 'recoveryCodes', codes: result.recoveryCodes });
  }

  async function confirmDisable() {
    await disable.mutateAsync({ password: disablePassword, code: disableCode });
    cancelDisable();
  }

  const requestError = requestEmailCode.error instanceof ApiError ? requestEmailCode.error.message : null;
  const setupError = setup.error instanceof ApiError ? setup.error.message : null;
  const enableError = enable.error instanceof ApiError ? enable.error.message : null;
  const disableError = disable.error instanceof ApiError ? disable.error.message : null;

  return (
    <SettingRow
      title="Authentification à deux facteurs"
      description={
        isEnabled
          ? "Activée : un code de votre application d'authentification est demandé à chaque connexion."
          : "Activer ou désactiver l'authentification à deux facteurs."
      }
      control={
        <Toggle
          label="Authentification à deux facteurs"
          checked={isEnabled}
          disabled={isBusy}
          onChange={(next) => {
            if (next) {
              void startEmailVerification().catch(() => undefined);
            } else {
              setShowDisableForm(true);
            }
          }}
        />
      }
    >
      {requestError && step.kind === 'idle' ? (
        <p role="alert" className="mt-2 text-xs font-medium text-danger-500">
          {requestError}
        </p>
      ) : null}

      {step.kind === 'awaitingEmailCode' ? (
        <div className="mt-4 flex max-w-md flex-col gap-3">
          <p className="text-xs text-ink-500">
            Un code à 6 chiffres vient d'être envoyé à votre adresse email. Il prouve l'accès à
            votre boîte mail avant de configurer l'application d'authentification.
          </p>
          <Field
            label="Code reçu par email"
            inputMode="numeric"
            autoComplete="one-time-code"
            autoFocus
            placeholder="123456"
            error={setupError ?? undefined}
            value={emailCode}
            onChange={(event) => {
              setEmailCode(event.target.value.replace(/\D/g, ''));
              setup.reset();
            }}
          />
          <div className="flex gap-3">
            <Button type="button" variant="secondary" size="sm" onClick={cancelEnrollment}>
              Annuler
            </Button>
            <Button
              size="sm"
              isLoading={setup.isPending}
              disabled={emailCode.length !== 6}
              onClick={() => void startEnrollment().catch(() => undefined)}
            >
              Continuer
            </Button>
          </div>
        </div>
      ) : null}

      {step.kind === 'enrolling' ? (
        <div className="mt-4 flex max-w-md flex-col gap-3">
          <p className="text-xs text-ink-500">
            Scannez ce QR code avec Google Authenticator, Authy ou 1Password — ou saisissez le
            secret manuellement si vous préférez — puis entrez le code généré.
          </p>
          <div className="flex justify-center rounded-input border border-ink-100 bg-white p-4">
            <QRCodeSVG value={step.provisioningUri} size={176} level="M" />
          </div>
          <div className="rounded-input border border-ink-100 bg-ink-50/50 p-3">
            <p className="text-xs text-ink-500">Secret (saisie manuelle)</p>
            <p className="mt-1 font-mono text-sm break-all text-ink-900">{step.secret}</p>
          </div>
          <Field
            label="Code à 6 chiffres"
            inputMode="numeric"
            autoComplete="one-time-code"
            autoFocus
            placeholder="123456"
            error={enableError ?? undefined}
            value={code}
            onChange={(event) => {
              setCode(event.target.value.replace(/\D/g, ''));
              enable.reset();
            }}
          />
          <div className="flex gap-3">
            <Button type="button" variant="secondary" size="sm" onClick={cancelEnrollment}>
              Annuler
            </Button>
            <Button
              size="sm"
              isLoading={enable.isPending}
              disabled={code.length !== 6}
              onClick={() => void confirmEnrollment().catch(() => undefined)}
            >
              Activer
            </Button>
          </div>
        </div>
      ) : null}

      {step.kind === 'recoveryCodes' ? (
        <div className="mt-4 flex max-w-md flex-col gap-3">
          <p className="text-xs text-ink-500">
            Conservez ces codes de récupération dans un endroit sûr : chacun ne fonctionne qu'une
            fois, et ils ne seront plus jamais affichés.
          </p>
          <div className="grid grid-cols-2 gap-2 rounded-input border border-ink-100 bg-ink-50/50 p-4 font-mono text-sm">
            {step.codes.map((recoveryCode) => (
              <span key={recoveryCode}>{recoveryCode}</span>
            ))}
          </div>
          <Button size="sm" className="self-start" onClick={() => setStep({ kind: 'idle' })}>
            J'ai noté mes codes
          </Button>
        </div>
      ) : null}

      {showDisableForm ? (
        <div className="mt-4 flex max-w-md flex-col gap-3">
          <p className="text-xs text-ink-500">
            Le mot de passe seul ne suffit pas à retirer ce facteur de sécurité : présentez aussi
            un code de votre application d'authentification (ou un code de récupération).
          </p>
          <Field
            label="Mot de passe"
            type="password"
            revealable
            autoComplete="current-password"
            autoFocus
            error={disableError ?? undefined}
            value={disablePassword}
            onChange={(event) => {
              setDisablePassword(event.target.value);
              disable.reset();
            }}
          />
          <Field
            label="Code de l'application d'authentification ou de récupération"
            autoComplete="one-time-code"
            placeholder="123456"
            value={disableCode}
            onChange={(event) => {
              setDisableCode(event.target.value.trim());
              disable.reset();
            }}
          />
          <div className="flex gap-3">
            <Button type="button" variant="secondary" size="sm" onClick={cancelDisable}>
              Annuler
            </Button>
            <Button
              size="sm"
              isLoading={disable.isPending}
              disabled={!disablePassword || !disableCode}
              onClick={() => void confirmDisable().catch(() => undefined)}
            >
              Désactiver la 2FA
            </Button>
          </div>
        </div>
      ) : null}
    </SettingRow>
  );
}

/**
 * « Changer le mot de passe », conforme à la maquette : les trois champs d'un coup, puis
 * « Sauvegarder ».
 *
 * La maquette s'arrête là, mais pas le parcours : le mot de passe actuel prouve l'identité,
 * puis un code à 6 chiffres envoyé par email prouve l'accès à la boîte mail avant que le
 * nouveau mot de passe ne s'applique. « Sauvegarder » envoie donc le mot de passe actuel et
 * ouvre la saisie du code ; le nouveau mot de passe attend en mémoire jusqu'à confirmation.
 * La révocation de toutes les sessions côté serveur (y compris la courante) déconnecte
 * l'utilisateur juste après — `useChangePassword` s'en charge via `clear()`.
 */
function ChangePasswordSection() {
  const email = useAuthStore((state) => state.user?.email ?? '');
  const requestChange = useRequestChangePassword();
  const changePassword = useChangePassword();

  const [step, setStep] = useState<'password' | 'code'>('password');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [code, setCode] = useState('');
  const [newPasswordError, setNewPasswordError] = useState<string | null>(null);
  const [confirmError, setConfirmError] = useState<string | null>(null);

  const isDirty = currentPassword !== '' || newPassword !== '' || confirmPassword !== '';
  const confirmMatches = confirmPassword !== '' && confirmPassword === newPassword;

  function resetAll() {
    setStep('password');
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setCode('');
    setNewPasswordError(null);
    setConfirmError(null);
    requestChange.reset();
    changePassword.reset();
  }

  async function submitPasswords() {
    // Mêmes règles que l'inscription et que le serveur : un refus annoncé ici évite
    // d'envoyer un code par email pour un mot de passe que l'API rejettera ensuite.
    const failed = firstUnsatisfiedRule(newPassword, email);

    if (failed !== null) {
      setNewPasswordError(failed.error(newPassword, email));
      return;
    }

    if (newPassword !== confirmPassword) {
      setConfirmError('Les mots de passe ne correspondent pas.');
      return;
    }

    await requestChange.mutateAsync({ currentPassword });
    setStep('code');
  }

  const requestError = requestChange.error instanceof ApiError ? requestChange.error.message : null;
  const changeError = changePassword.error instanceof ApiError ? changePassword.error.message : null;

  if (changePassword.isSuccess) {
    return (
      <SettingRow
        title="Mot de passe modifié"
        description="Vous avez été déconnecté de toutes vos sessions par sécurité. Reconnectez-vous avec votre nouveau mot de passe."
      />
    );
  }

  return (
    <SettingRow title="Changer le mot de passe">
      {step === 'password' ? (
        <div className="mt-4 flex flex-col gap-3">
          {/* La colonne de droite (w-6) reçoit la coche de confirmation de la maquette ;
              elle est réservée sur chaque ligne pour que les trois champs restent alignés. */}
          <div className="flex max-w-md items-center gap-3 pr-9">
            <Field
              label="Mot de passe actuel"
              type="password"
              revealable
              autoComplete="current-password"
              className="flex-1"
              error={requestError ?? undefined}
              value={currentPassword}
              onChange={(event) => {
                setCurrentPassword(event.target.value);
                requestChange.reset();
              }}
            />
          </div>
          <div className="flex max-w-md items-center gap-3 pr-9">
            <Field
              label="Nouveau mot de passe"
              type="password"
              revealable
              autoComplete="new-password"
              className="flex-1"
              error={newPasswordError ?? undefined}
              value={newPassword}
              onChange={(event) => {
                setNewPassword(event.target.value);
                setNewPasswordError(null);
                setConfirmError(null);
              }}
            />
          </div>
          <div className="flex max-w-md items-center gap-3">
            <Field
              label="Confirmez le mot de passe"
              type="password"
              revealable
              autoComplete="new-password"
              className="flex-1"
              error={confirmError ?? undefined}
              value={confirmPassword}
              onChange={(event) => {
                setConfirmPassword(event.target.value);
                setConfirmError(null);
              }}
            />
            <span className="grid w-6 shrink-0 place-items-center">
              {confirmMatches ? (
                <span className="grid size-6 place-items-center rounded-full bg-ink-900 text-white">
                  <Check aria-label="Les mots de passe correspondent" className="size-3.5" />
                </span>
              ) : null}
            </span>
          </div>
        </div>
      ) : (
        <div className="mt-4 flex max-w-md flex-col gap-3">
          <p className="text-xs text-ink-500">
            Un code à 6 chiffres vient d'être envoyé à votre adresse email. Toutes vos sessions
            seront déconnectées une fois le changement confirmé.
          </p>
          <Field
            label="Code reçu par email"
            inputMode="numeric"
            autoComplete="one-time-code"
            autoFocus
            placeholder="123456"
            error={changeError ?? undefined}
            value={code}
            onChange={(event) => {
              setCode(event.target.value.replace(/\D/g, ''));
              changePassword.reset();
            }}
          />
        </div>
      )}

      <div className="mt-8 flex items-center justify-end gap-4">
        <Button
          type="button"
          variant="ghost"
          disabled={!isDirty || requestChange.isPending || changePassword.isPending}
          onClick={resetAll}
        >
          Annuler
        </Button>

        {step === 'password' ? (
          <Button
            type="button"
            className="min-w-40"
            isLoading={requestChange.isPending}
            disabled={!currentPassword || !newPassword || !confirmPassword}
            onClick={() => void submitPasswords().catch(() => undefined)}
          >
            Sauvegarder
          </Button>
        ) : (
          <Button
            type="button"
            className="min-w-40"
            isLoading={changePassword.isPending}
            disabled={code.length !== 6}
            onClick={() => void changePassword.mutateAsync({ code, newPassword }).catch(() => undefined)}
          >
            Confirmer
          </Button>
        )}
      </div>
    </SettingRow>
  );
}

function SecuriteTab() {
  return (
    <div className="flex max-w-2xl flex-col">
      <TwoFactorSection />

      <SettingRow
        title="Vérification SMS"
        badge="Bientôt"
        description={
          <>
            Recevoir un code de vérification à 6 chiffres par SMS
            <br className="hidden sm:inline" /> ou WhatsApp selon l'indicatif de votre pays.
          </>
        }
        control={<Toggle label="Vérification SMS" checked={false} disabled />}
      />

      <ChangePasswordSection />
    </div>
  );
}

function EquipeTab() {
  const users = useUsers({ pageSize: 20 });

  return (
    <div>
      <h3 className="font-semibold text-ink-900">Gestion de votre équipe</h3>
      <p className="text-sm text-ink-500">Ajoutez, gérez et supprimez les membres ayant accès à votre espace Pixlide.</p>

      {users.isPending ? <LoadingState /> : null}

      {users.isError ? (
        <ErrorState
          message="Impossible de charger les membres de l'équipe pour le moment."
          onRetry={() => void users.refetch()}
        />
      ) : null}

      {users.data && users.data.items.length > 0 ? (
        <div className="mt-4 overflow-x-auto rounded-input border border-ink-100">
          <table className="w-full text-left text-sm">
            <thead className="bg-ink-50/70 text-xs font-medium tracking-wide text-ink-500 uppercase">
              <tr>
                <th className="px-4 py-3">Membre</th>
                <th className="px-4 py-3">Rôle</th>
                <th className="px-4 py-3">Ajouté le</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-ink-100/70">
              {users.data.items.map((member) => (
                <tr key={member.id}>
                  <td className="px-4 py-3">
                    <p className="font-medium text-ink-900">
                      {member.firstName} {member.lastName}
                    </p>
                    <p className="text-xs text-ink-500">{member.email}</p>
                  </td>
                  <td className="px-4 py-3">
                    <Badge tone="brand">{ROLE_LABELS[member.role] ?? `Rôle ${member.role}`}</Badge>
                  </td>
                  <td className="px-4 py-3 text-ink-500">{formatDate(member.createdAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      {users.data?.items.length === 0 ? (
        <p className="mt-4 text-sm text-ink-500">Aucun membre d'équipe pour le moment.</p>
      ) : null}
    </div>
  );
}

/**
 * Paramètres du compte : profil, préférences, sécurité et équipe.
 *
 * Profil (modifiable, avec son propre « Sauvegarder »), Équipe et la 2FA/changement de mot
 * de passe (onglet Sécurité) s'appuient sur des données réelles. L'onglet Préférences n'a
 * pas d'API dédiée aujourd'hui et reste un aperçu visuel fidèle à la maquette, sans bouton
 * d'enregistrement — mieux vaut ne rien proposer que simuler une sauvegarde qui ne
 * sauvegarde rien.
 */
export function ParametresPage() {
  const [activeTab, setActiveTab] = useState<TabKey>('profil');

  return (
    <div className="mx-auto flex max-w-5xl flex-col gap-6">
      <SectionHeader title="Paramètres" />

      <Card className="p-6">
        <div role="tablist" className="-mx-6 flex overflow-x-auto border-b border-ink-100/70 px-6">
          {TABS.map((tab) => {
            const Icon = tab.icon;
            const isActive = tab.key === activeTab;

            return (
              <button
                key={tab.key}
                type="button"
                role="tab"
                aria-selected={isActive}
                onClick={() => setActiveTab(tab.key)}
                className={`-mb-px flex shrink-0 items-center gap-2 border-b-2 px-5 pb-3 text-sm transition ${
                  isActive
                    ? 'border-ink-900 font-semibold text-ink-900'
                    : 'border-transparent font-medium text-ink-300 hover:text-ink-700'
                }`}
              >
                <Icon aria-hidden className="size-4" />
                {tab.label}
              </button>
            );
          })}
        </div>

        <div className="pt-6">
          {activeTab === 'profil' ? <ProfilTab /> : null}
          {activeTab === 'preferences' ? <PreferencesTab /> : null}
          {activeTab === 'securite' ? <SecuriteTab /> : null}
          {activeTab === 'equipe' ? <EquipeTab /> : null}
        </div>
      </Card>
    </div>
  );
}
